import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { listEventFeedback, listEventUpdates } from "@/api/events";
import {
  deleteEventUpdate,
  editEventUpdate,
  getHostWaitlist,
  offerNextOnWaitlist,
  respondToFeedback,
} from "@/api/organiser-desk";

/**
 * The host's tools on one event: posted updates edited, pinned or taken back.
 * Every action needs `manage`: a door helper is refused, in the server's own
 * words.
 */
export function HostToolsPanel({ eventId }: { eventId: string }) {
  return (
    <div className="space-y-4">
      <UpdatesTools eventId={eventId} />
      <WaitlistTools eventId={eventId} />
      <FeedbackReplies eventId={eventId} />
    </div>
  );
}

function UpdatesTools({ eventId }: { eventId: string }) {
  const qc = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; body: string } | null>(null);
  const { data } = useQuery({ queryKey: ["host-updates", eventId], queryFn: () => listEventUpdates(eventId, { limit: 20 }) });
  const updates = data?.data.items ?? [];

  async function run(work: () => Promise<unknown>, done: string) {
    setMsg(null);
    try {
      await work();
      setMsg(done);
      setEditing(null);
      await qc.invalidateQueries({ queryKey: ["host-updates", eventId] });
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "That did not work");
    }
  }

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-gray-700">Updates ({updates.length}) — edit, pin, take back</h3>
      <ul className="text-xs text-gray-700 space-y-1">
        {updates.map((u) => (
          <li key={u.id} className="flex gap-2 items-center flex-wrap">
            <span className="font-mono">{u.updateType}</span>
            {u.isPinned && <span className="text-amber-700">pinned</span>}
            {editing?.id === u.id ? (
              <>
                <input className="input w-80" value={editing.body} onChange={(e) => setEditing({ id: u.id, body: e.target.value })} />
                <button className="underline" onClick={() => void run(() => editEventUpdate(eventId, u.id, { body: editing.body }), "Edited")}>save</button>
              </>
            ) : (
              <>
                <span>{u.body}</span>
                <button className="underline" onClick={() => setEditing({ id: u.id, body: u.body })}>edit</button>
                <button className="underline" onClick={() => void run(() => editEventUpdate(eventId, u.id, { isPinned: !u.isPinned }), u.isPinned ? "Unpinned" : "Pinned")}>
                  {u.isPinned ? "unpin" : "pin"}
                </button>
                <button className="underline text-red-700" onClick={() => void run(() => deleteEventUpdate(eventId, u.id), "Taken back")}>
                  take back
                </button>
              </>
            )}
          </li>
        ))}
      </ul>
      {msg && <p className="text-xs text-gray-600">{msg}</p>}
    </div>
  );
}

/** Who waits for a full event, in order, and a seat offered by hand to the head of the line. */
function WaitlistTools({ eventId }: { eventId: string }) {
  const qc = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);
  const { data, error } = useQuery({ queryKey: ["host-waitlist", eventId], queryFn: () => getHostWaitlist(eventId), retry: false });
  const line = data?.data;

  async function offer() {
    setMsg(null);
    try {
      const res = await offerNextOnWaitlist(eventId);
      setMsg(`Held for ${res.data.user.name} until ${new Date(res.data.offerExpiresAt ?? "").toLocaleString()}`);
      await qc.invalidateQueries({ queryKey: ["host-waitlist", eventId] });
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "That did not work");
    }
  }

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-gray-700">
        Waitlist {line ? `· ${line.waitingCount} waiting for ${line.seatsWaiting} seats · ${line.offeredCount} holding` : ""}
      </h3>
      {error && <p className="text-xs text-red-700">{(error as Error).message}</p>}
      <ol className="text-xs text-gray-700 space-y-1">
        {line?.entries.map((e) => (
          <li key={e.id}>
            {e.position ?? "—"} · {e.user.name} · {e.seats} seat(s) · {e.status}
            {e.offerExpiresAt ? ` until ${new Date(e.offerExpiresAt).toLocaleString()}` : ""}
          </li>
        ))}
      </ol>
      <button className="px-3 py-1 rounded border border-gray-300 text-sm" onClick={() => void offer()}>
        Offer a seat to the next person
      </button>
      {msg && <p className="text-xs text-gray-600">{msg}</p>}
    </div>
  );
}

/** Every rating with a Reply under it. A reply is written once and signed as the host. */
function FeedbackReplies({ eventId }: { eventId: string }) {
  const qc = useQueryClient();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<string | null>(null);
  const { data } = useQuery({ queryKey: ["host-feedback", eventId], queryFn: () => listEventFeedback(eventId, { limit: 20 }) });
  const items = (data?.data.items ?? []) as Array<{
    id: string;
    overallRating: number;
    comment: string | null;
    organizerResponse: string | null;
    respondedAs?: { name: string } | null;
  }>;

  async function send(feedbackId: string) {
    setMsg(null);
    try {
      const res = await respondToFeedback(eventId, feedbackId, drafts[feedbackId] ?? "");
      setMsg(`Replied as ${res.data.respondedAs?.name ?? "the host"}`);
      await qc.invalidateQueries({ queryKey: ["host-feedback", eventId] });
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "That did not work");
    }
  }

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-gray-700">Ratings ({items.length}) — reply once</h3>
      <ul className="text-xs text-gray-700 space-y-2">
        {items.map((f) => (
          <li key={f.id}>
            <div>
              {f.overallRating}★ {f.comment ?? ""}
            </div>
            {f.organizerResponse ? (
              <div className="pl-3 text-gray-600">
                {f.respondedAs?.name ?? "Host"}: {f.organizerResponse}
              </div>
            ) : (
              <div className="flex gap-2 pl-3">
                <input
                  className="input w-80"
                  maxLength={2000}
                  placeholder="Reply"
                  value={drafts[f.id] ?? ""}
                  onChange={(e) => setDrafts({ ...drafts, [f.id]: e.target.value })}
                />
                <button className="underline" disabled={!(drafts[f.id] ?? "").trim()} onClick={() => void send(f.id)}>
                  reply
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
      {msg && <p className="text-xs text-gray-600">{msg}</p>}
    </div>
  );
}
